import { useState, useCallback } from 'react';
import { searchTitleByProperty, checkTitleStatus as checkTitleStatusApi } from '../services/titleApi';
import { createTransactionRecord, saveTransaction } from '../services/datatraceTransactions';

/**
 * Custom hook for managing title search workflow (TIQSA)
 * Handles both initiating async title searches and polling for results
 * Saves all transactions to persistent history for auditing (same as DataTrace API Tester)
 */
export const useTitleSearch = () => {
  const [titleSearchStatus, setTitleSearchStatus] = useState('not-started'); // not-started, pending, polling, completed, needs-review, error
  const [titleSearchData, setTitleSearchData] = useState(null);
  const [titleSearchError, setTitleSearchError] = useState(null);
  const [titleTransactionId, setTitleTransactionId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  /**
   * Start a new title search
   * @param {Object} searchParams - Search parameters (address, city, state, zip, county, apn)
   * @param {string} productType - TIQSA product type: 'current_owner_bronze' or 'current_owner_silver'
   * @returns {Promise<Object>} - Search response with transactionId
   */
  const startTitleSearch = useCallback(async (searchParams, productType = 'current_owner_bronze') => {
    setTitleSearchStatus('pending');
    setTitleSearchError(null);
    setTitleSearchData(null);

    try {
      const response = await searchTitleByProperty(searchParams, productType);

      // TIQSA is always async, so it should have a transaction ID
      if (response.transactionId) {
        setTitleTransactionId(response.transactionId);
        setTitleSearchStatus('polling');
        setTitleSearchData({
          transactionId: response.transactionId,
          confirmationNumber: response.confirmationNumber,
          status: response.status || 'PENDING',
          productType: productType,
          timestamp: new Date().toISOString(),
          searchParams
        });

        // Save to transaction history
        try {
          const productConfig = {
            productCode: 'tiqsa',
            productType: productType,
            endpoint: 'tiqsa'
          };
          const transaction = createTransactionRecord(
            searchParams,
            productConfig,
            { data: response.rawResponse }, // Wrap in same format as API response
            response.requestBody // Request body from title API
          );
          await saveTransaction(transaction);
          console.log('Title search transaction saved to history:', transaction.transactionId);
        } catch (saveError) {
          console.error('Failed to save title transaction to history:', saveError);
          // Don't fail the search if transaction save fails
        }

        return response;
      }

      // Unexpected response format
      throw new Error('Unexpected response format from title search API');
    } catch (error) {
      console.error('Title search error:', error);
      setTitleSearchStatus('error');
      setTitleSearchError(error.message || 'Failed to initiate title search');
      setTitleSearchData({ error: true, errorMessage: error.message });
      throw error;
    }
  }, []);

  /**
   * Check the status of a pending title search transaction
   * @param {string} transactionId - Optional transaction ID (uses current if not provided)
   * @returns {Promise<Object>} - Status response
   */
  const checkTitleStatus = useCallback(async (transactionId = null) => {
    const txnId = transactionId || titleTransactionId;

    if (!txnId) {
      throw new Error('No transaction ID available for status check');
    }

    setIsPolling(true);

    try {
      const statusResponse = await checkTitleStatusApi(txnId);

      // Update data with latest status
      setTitleSearchData(prev => ({
        ...prev,
        status: statusResponse.status,
        statusResponse: statusResponse,
        lastChecked: new Date().toISOString()
      }));

      // Check if completed
      const status = (statusResponse.status || '').toLowerCase().trim();
      const isCompleted = status.includes('completed') ||
                         status.includes('success') ||
                         status.includes('available') ||
                         status.includes('fulfilled');

      if (isCompleted) {
        setTitleSearchStatus('completed');
        // If response includes the actual report data, save it
        if (statusResponse.data || statusResponse.report) {
          setTitleSearchData(prev => ({
            ...prev,
            reportData: statusResponse.data || statusResponse.report
          }));
        }
      } else if (status.includes('error') || status.includes('failed')) {
        setTitleSearchStatus('needs-review');
        setTitleSearchError(statusResponse.message || 'Title search failed');
      } else {
        setTitleSearchStatus('polling');
      }

      setIsPolling(false);
      return statusResponse;
    } catch (error) {
      console.error('Title status check error:', error);
      setIsPolling(false);
      setTitleSearchError(error.message || 'Failed to check title search status');
      throw error;
    }
  }, [titleTransactionId]);

  /**
   * Get the report for a completed title search
   * @param {string} transactionId - Optional transaction ID (uses current if not provided)
   * @param {string} productCode - Product code for the report
   * @returns {Promise<Object>} - Report data
   */
  const getTitleReport = useCallback(async (transactionId = null, productCode = 'tiqsa') => {
    const txnId = transactionId || titleTransactionId;

    if (!txnId) {
      throw new Error('No transaction ID available for report retrieval');
    }

    try {
      // Import getReport from titleApi if it exists
      const { getReport } = await import('../services/titleApi');
      const reportData = await getReport(txnId, productCode);

      setTitleSearchData(prev => ({
        ...prev,
        reportData
      }));

      return reportData;
    } catch (error) {
      console.error('Title report retrieval error:', error);
      setTitleSearchError(error.message || 'Failed to retrieve title report');
      throw error;
    }
  }, [titleTransactionId]);

  /**
   * Reset the title search state
   */
  const resetTitleSearch = useCallback(() => {
    setTitleSearchStatus('not-started');
    setTitleSearchData(null);
    setTitleSearchError(null);
    setTitleTransactionId(null);
    setIsPolling(false);
  }, []);

  return {
    // State
    titleSearchStatus,
    titleSearchData,
    titleSearchError,
    titleTransactionId,
    isPolling,

    // Actions
    startTitleSearch,
    checkTitleStatus,
    getTitleReport,
    resetTitleSearch
  };
};

export default useTitleSearch;
